'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

// Proper TypeScript interfaces
export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  role: 'patient' | 'admin';
  is_active: boolean;
  tenant_id?: string;
  created_at: string;
  updated_at: string;
}

export interface SignUpMetadata {
  first_name?: string;
  last_name?: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  error: AuthError | Error | null;
  signUp: (email: string, password: string, metadata?: SignUpMetadata) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isEmailVerified: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Singleton Supabase client for auth operations
let supabaseInstance: ReturnType<typeof createClient> | null = null;

function getSupabase() {
  if (!supabaseInstance) {
    supabaseInstance = createClient();
  }
  return supabaseInstance;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | Error | null>(null);

  const supabase = useMemo(() => getSupabase(), []);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        // Profile might not exist yet for new users
        if (profileError.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching profile:', profileError);
        return null;
      }

      return data as UserProfile;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      return null;
    }
  }, [supabase]);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }

        if (mounted) {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);

          if (initialSession?.user) {
            const userProfile = await fetchProfile(initialSession.user.id);
            setProfile(userProfile);
          }

          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err as AuthError);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          // Fetch profile on auth change
          const userProfile = await fetchProfile(currentSession.user.id);
          setProfile(userProfile);
        } else {
          setProfile(null);
        }

        // Clear error on successful auth events
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setError(null);
        }

        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, fetchProfile]);

  // Sign up with email/password
  const signUp = useCallback(async (
    email: string, 
    password: string, 
    metadata: SignUpMetadata = {}
  ): Promise<{ user: User | null; error: AuthError | null }> => {
    try {
      setError(null);
      
      const fullName = metadata.first_name && metadata.last_name 
        ? `${metadata.first_name} ${metadata.last_name}`.trim()
        : '';

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            ...metadata,
          },
          emailRedirectTo: typeof window !== 'undefined' 
            ? `${window.location.origin}/auth/callback`
            : undefined,
        },
      });

      if (signUpError) {
        setError(signUpError);
        return { user: null, error: signUpError };
      }

      return { user: data.user, error: null };
    } catch (err) {
      const error = err as AuthError;
      setError(error);
      return { user: null, error };
    }
  }, [supabase]);

  // Sign in with email/password
  const signIn = useCallback(async (
    email: string, 
    password: string
  ): Promise<{ user: User | null; error: AuthError | null }> => {
    try {
      setError(null);

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError);
        return { user: null, error: signInError };
      }

      return { user: data.user, error: null };
    } catch (err) {
      const error = err as AuthError;
      setError(error);
      return { user: null, error };
    }
  }, [supabase]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const { error: signOutError } = await supabase.auth.signOut();
      
      if (signOutError) {
        throw signOutError;
      }

      setUser(null);
      setSession(null);
      setProfile(null);
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  }, [supabase]);

  // Reset password
  const resetPassword = useCallback(async (
    email: string
  ): Promise<{ error: AuthError | null }> => {
    try {
      setError(null);

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback?next=/reset-password`
          : undefined,
      });

      if (resetError) {
        setError(resetError);
        return { error: resetError };
      }

      return { error: null };
    } catch (err) {
      const error = err as AuthError;
      setError(error);
      return { error };
    }
  }, [supabase]);

  // Update password
  const updatePassword = useCallback(async (
    newPassword: string
  ): Promise<{ error: AuthError | null }> => {
    try {
      setError(null);

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError);
        return { error: updateError };
      }

      return { error: null };
    } catch (err) {
      const error = err as AuthError;
      setError(error);
      return { error };
    }
  }, [supabase]);

  // Refresh session
  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      const { error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        throw refreshError;
      }
    } catch (err) {
      setError(err as AuthError);
    }
  }, [supabase]);

  // Refresh profile
  const refreshProfile = useCallback(async (): Promise<void> => {
    if (user?.id) {
      const userProfile = await fetchProfile(user.id);
      setProfile(userProfile);
    }
  }, [user, fetchProfile]);

  // Memoized computed values
  const isEmailVerified = useMemo(() => {
    return user?.email_confirmed_at != null;
  }, [user]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    session,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    refreshProfile,
    isEmailVerified,
  }), [
    user,
    session,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    refreshProfile,
    isEmailVerified,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Export type for external use
export type { AuthContextType };
